// apps/web/src/routes/PublicPages.tsx
//
// Public status + error pages from reference/urls.md: /401, /403, /404,
// /429, /500, /503, /access-denied, /account-locked, /accept-invite/:token.

import { Link, useParams } from 'react-router-dom';

interface StatusPageProps {
  code: string;
  title: string;
  message: string;
}

function StatusPage({ code, title, message }: StatusPageProps): JSX.Element {
  return (
    <div className="status-page">
      <h1>{code}</h1>
      <h2>{title}</h2>
      <p>{message}</p>
      <div className="status-actions">
        <Link to="/" className="btn btn-primary">Go home</Link>
        <Link to="/signin" className="btn btn-secondary">Sign in</Link>
      </div>
    </div>
  );
}

export function Page401() {
  return <StatusPage code="401" title="Not authenticated" message="You need to sign in to view this page." />;
}

export function Page403() {
  return <StatusPage code="403" title="Forbidden" message="You don't have permission to access this resource." />;
}

export function Page404() {
  return <StatusPage code="404" title="Not found" message="This page doesn't exist." />;
}

export function Page429() {
  return <StatusPage code="429" title="Too many requests" message="Slow down — you've hit the rate limit. Try again soon." />;
}

export function Page500() {
  return <StatusPage code="500" title="Server error" message="Something went wrong on our side. Please retry." />;
}

export function Page503() {
  return <StatusPage code="503" title="Unavailable" message="The service is temporarily down for maintenance." />;
}

export function AccessDenied() {
  return <StatusPage code="403" title="Access denied" message="Your account does not have access to this area." />;
}

export function AccountLocked() {
  return <StatusPage code="423" title="Account locked" message="Too many failed attempts. Your account is temporarily locked." />;
}

export function AcceptInvite() {
  const { token } = useParams();
  return (
    <div className="status-page">
      <h1>Invite</h1>
      <h2>You&apos;ve been invited to a workspace</h2>
      <p>
        Invite token: <code>{token ?? '…'}</code>
      </p>
      <p className="muted">
        Accepting will add you to the tenant. You&apos;ll then be able to sign in and manage your campaign.
      </p>
      <div className="status-actions">
        <Link to="/signin" className="btn btn-primary">Sign in to accept</Link>
        <Link to="/" className="btn btn-secondary">Not right now</Link>
      </div>
    </div>
  );
}